import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { CmpsOverlayTriggerDirective } from '@cmps/ng-uikit';
import { forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import { catchErrorLogRet } from '../../../common/rest.operators';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { PeopleOfInterestUserService } from '../../people-of-interest.user.service';
import { SearchUserConfigService } from '../../search-user.config.service';
import { UserService } from '../../user.service';

import { TranslationKeyMap } from './dialog.component.i18n';
import { groupUsers } from './function/group-users.function';
import { ViewUser } from './model/view-user.model';

@Component({
    selector: 'cmps-quick-access',
    templateUrl: './dialog.component.html',
    host: {
        class: 'cmps-quick-access',
    },
})
@TakeUntilDestroy()
export class DialogComponent implements OnInit, OnDestroy {
    @Input() overlay: CmpsOverlayTriggerDirective;
    @Output() userSelect: EventEmitter<number>;

    /** @private */
    readonly _i18n = TranslationKeyMap;

    /** @private */
    _users: ViewUser[];

    constructor(
        private readonly _userSearchConfigService: SearchUserConfigService,
        private readonly _poiUserService: PeopleOfInterestUserService,
        private readonly _userService: UserService,
        private readonly _authService: AuthService,
    ) {
        this.userSelect = new EventEmitter();
    }

    ngOnInit(): void {
        this._load();
    }

    ngOnDestroy(): void {
        this.userSelect.complete();
    }

    /** @private */
    _onUserClick(user: ViewUser): void {
        this.userSelect.emit(user.userId);
        this.overlay?.close();
    }

    private _load(): void {
        forkJoin([
            of(null)
                .pipe(
                    this._poiUserService.list(
                        () => this._userSearchConfigService.scopeId,
                    ),
                )
                .pipe(
                    map((ret) =>
                        ret
                            .filter((e) => e.isAvailable)
                            .map<ViewUser>((e) => ({
                                userId: e.userId,
                                name: `${e.firstName?.trim()} ${e.lastName?.trim()}`,
                                signedInUser: false,
                                favourite: true,
                            })),
                    ),
                ),
            this._userService
                .listHierarchy('subordinates', this._authService.user.id)
                .pipe(
                    map((ret) =>
                        ret
                            .filter((e) => e.endDate == null)
                            .map<ViewUser>((e) => ({
                                userId: e.userId,
                                name: e.managerName, // yes, managerName. see UserHierarchyModelTransformer
                                signedInUser: false,
                                favourite: false,
                            })),
                    ),
                    catchErrorLogRet([]),
                ),
        ])
            .pipe(
                tap((ret) => {
                    const signedInUser: ViewUser = {
                        userId: this._authService.user.id,
                        name: `${this._authService.user.firstName?.trim()} ${this._authService.user.lastName?.trim()}`,
                        signedInUser: true,
                        favourite: false,
                    };

                    this._users = groupUsers(signedInUser, ret[0], ret[1]);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
